import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  EmptyState,
  Icon,
  IconButton,
  Input,
  Menu,
  Modal,
  PageBody,
  Pill,
  Select,
  SkeletonText,
  StatTile,
  Tabs,
  useToast,
} from "@astrum/ui";
import { ExportMenu } from "../components/ExportMenu.js";
import { WorkspaceSettings } from "../components/WorkspaceSettings.js";
import { MailSettings } from "../components/MailSettings.js";
import { TwoFactorPolicy } from "../components/TwoFactorPolicy.js";
import { UserGroupsAdmin } from "../components/UserGroupsAdmin.js";
import { AdminTeams } from "../components/AdminTeams.js";
import { api, ApiError } from "../lib/api.js";
import { downloadExcel, excelFileName } from "../lib/export-excel.js";
import { useAuth } from "../lib/auth.js";
import { BudgetRatesAdmin } from "../components/BudgetRatesAdmin.js";
import { useWorkspace } from "../lib/workspace.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./admin.css";

/**
 * Workspace administration: people, invitations, and the audit log.
 *
 * Every endpoint behind this screen existed from Phase 7 with no way in —
 * most importantly invitations, which meant an administrator could not
 * onboard a colleague without running a script on the server.
 *
 * The self-lockout rules are enforced server-side; this screen mirrors them
 * so the reason is visible before the request rather than as an error after.
 */

type AdminUser = {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  jobTitle: string | null;
  department: string | null;
  role: string;
  isActive: boolean;
  twoFactorEnabled: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  managerId: string | null;
  checkInRequired: boolean;
};

type Invitation = {
  id: string;
  email: string;
  role: string;
  invitedByName: string;
  acceptedAt: string | null;
  expiresAt: string;
  createdAt: string;
};

type AuditEvent = {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  metadata: unknown;
  ipAddress: string | null;
  createdAt: string;
};

type Stats = {
  users: { total: number; active: number; admins: number; twoFactor: number };
  pendingInvitations: number;
};

const ROLES = ["admin", "manager", "editor", "member", "viewer"] as const;

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrator",
  manager: "Manager",
  editor: "Editor",
  member: "Member",
  viewer: "Viewer",
};

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h ago`;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** "admin.invitation.created" reads better as "Invitation created". */
function humaniseAction(action: string): string {
  const tail = action.replace(/^admin\./, "").replace(/_/g, " ");
  const words = tail.split(".").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ─────────────────────────────── audit filters ─────────────────────────────── */

type AuditFilters = { action: string; actorId: string; resourceType: string; from: string; to: string };
const EMPTY_AUDIT_FILTERS: AuditFilters = { action: "", actorId: "", resourceType: "", from: "", to: "" };

/** The query string the list and both exports share. */
function auditQuery(filters: AuditFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value.trim()) params.set(key, value.trim());
  }
  return params.toString();
}

function AuditFilterBar({
  filters,
  onChange,
  people,
}: {
  filters: AuditFilters;
  onChange: (next: AuditFilters) => void;
  people: AdminUser[];
}) {
  const set = (patch: Partial<AuditFilters>) => onChange({ ...filters, ...patch });
  const active = auditQuery(filters) !== "";
  return (
    <div className="admin__audit-filters" role="search" aria-label="Filter the audit log">
      <Input label="Action" placeholder="e.g. auth.login" value={filters.action} onChange={(e) => set({ action: e.target.value })} />
      <Select label="Actor" value={filters.actorId} onChange={(e) => set({ actorId: e.target.value })}>
        <option value="">Anyone</option>
        {people.map((person) => (
          <option key={person.id} value={person.id}>
            {person.displayName}
          </option>
        ))}
      </Select>
      <Input
        label="Resource type"
        placeholder="e.g. user"
        value={filters.resourceType}
        onChange={(e) => set({ resourceType: e.target.value })}
      />
      <Input label="From" type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set({ from: e.target.value })} />
      <Input label="To" type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set({ to: e.target.value })} />
      {active && (
        <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_AUDIT_FILTERS)}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

/* ─────────────────────────────── invite dialog ─────────────────────────────── */

/**
 * The one-time link, with the warning that it is one-time.
 *
 * Shared by creating an invitation and reissuing one, so the two cannot come
 * to describe the same token differently — the warning is the important part,
 * and a second copy of it would eventually drift from this one.
 */
function InviteLink({
  email,
  link,
  onClose,
  reissued,
}: {
  email: string;
  link: string;
  onClose: () => void;
  reissued?: boolean;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  return (
    <div className="admin-invite">
      <p className="admin-invite__done">
        <Icon name="check" size={15} />
        {reissued ? (
          <>
            New link for <strong>{email}</strong>.
          </>
        ) : (
          <>
            Invitation created for <strong>{email}</strong>.
          </>
        )}
      </p>

      <label className="admin-invite__link-label" htmlFor="invite-link">
        Send them this link
      </label>
      <div className="admin-invite__link">
        <input id="invite-link" readOnly value={link} onFocus={(e) => e.target.select()} />
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            navigator.clipboard
              .writeText(link)
              .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              })
              .catch(() => toast("Could not copy — select the link and copy it manually.", { tone: "danger" }));
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>

      <p className="admin-invite__warning">
        <Icon name="warning" size={13} />
        This link is shown once and cannot be retrieved later. There is no mail delivery configured yet, so send it
        yourself.
        {reissued ? " Any earlier link for this person has stopped working." : " It expires in seven days."}
      </p>

      <div className="admin-invite__actions">
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

function InviteDialog({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const { workspaces } = useWorkspace();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  // The office to place them in. Empty string means "the default office", which
  // the server resolves — so a single-office workspace need not think about it.
  const [officeId, setOfficeId] = useState("");
  const [sending, setSending] = useState(false);
  /** The one-time link, shown after the invitation is created. */
  const [link, setLink] = useState<string | null>(null);

  const invite = useCallback(() => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    api
      .post<{ invitation: { id: string }; token: string }>("/admin/invitations", {
        email: trimmed,
        role,
        ...(officeId ? { workspaceId: officeId } : {}),
      })
      .then((res) => {
        setLink(`${window.location.origin}/invite/${res.token}`);
        onInvited();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not create that invitation.", { tone: "danger" });
      })
      .finally(() => setSending(false));
  }, [email, role, officeId, onInvited, toast]);

  /*
   * The link is shown once and never again — the raw token is not stored, only
   * its hash. An administrator who closes this without copying reissues from
   * the Invitations tab rather than revoking and starting over.
   */
  if (link) {
    return <InviteLink email={email.trim()} link={link} onClose={onClose} />;
  }

  return (
    <form
      className="admin-invite"
      onSubmit={(e) => {
        e.preventDefault();
        invite();
      }}
    >
      <Input
        label="Email address"
        type="email"
        placeholder="colleague@astrum.local"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoFocus
        required
      />

      <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
        {ROLES.map((r) => (
          // Only an administrator may mint another administrator; the server
          // enforces this, and offering it would be a promise it will refuse.
          <option key={r} value={r} disabled={r === "admin" && viewer?.role !== "admin"}>
            {ROLE_LABEL[r]}
          </option>
        ))}
      </Select>

      {/* Office picker, only where there is a choice to make. The chosen role
          above is the role they hold in this office. */}
      {workspaces.length > 1 && (
        <Select label="Office" value={officeId} onChange={(e) => setOfficeId(e.target.value)}>
          <option value="">Default office</option>
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
      )}

      <div className="admin-invite__actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={sending} disabled={!email.trim() || sending}>
          Create invitation
        </Button>
      </div>
    </form>
  );
}

/* ────────────────────────── temporary password ────────────────────────── */

/** Words rather than symbols: this gets read aloud or typed from a note. */
const PASSWORD_WORDS = [
  "harbour", "lantern", "meadow", "compass", "thicket", "granite", "kestrel", "orchard",
  "willow", "cinder", "beacon", "marble", "juniper", "pebble", "saffron", "vellum",
];

function generateTemporaryPassword(): string {
  const bytes = new Uint32Array(4);
  crypto.getRandomValues(bytes);
  const words = [...bytes].slice(0, 3).map((n) => PASSWORD_WORDS[n % PASSWORD_WORDS.length]);
  // A number on the end so it clears any policy that expects one, without
  // making the phrase harder to relay.
  return `${words.join("-")}-${bytes[3]! % 100}`;
}

interface ProfileDialogProps {
  user: AdminUser;
  allUsers: AdminUser[];
  onSave: (
    changes: Partial<Pick<AdminUser, "department" | "jobTitle" | "managerId" | "checkInRequired">>,
  ) => void;
  onClose: () => void;
  saving: boolean;
}

/**
 * The profile fields an administrator sets for a person — department, job
 * title, who they report to, and whether they file a daily report. Kept in a
 * dialog rather than the table row because these inputs (two text fields, a
 * manager picker, a checkbox) do not fit a fixed-width grid column without
 * cramping the row every other action lives in. Department and job title show
 * on the People directory, so this is where they are actually set.
 */
function ProfileDialog({ user, allUsers, onSave, onClose, saving }: ProfileDialogProps) {
  const [department, setDepartment] = useState(user.department ?? "");
  const [jobTitle, setJobTitle] = useState(user.jobTitle ?? "");
  const [managerId, setManagerId] = useState(user.managerId ?? "");
  const [checkInRequired, setCheckInRequired] = useState(user.checkInRequired);

  // Nobody can manage themselves, and only active people are worth choosing
  // from — an inactive account managing someone is a dead end.
  const managerOptions = allUsers.filter((u) => u.id !== user.id && u.isActive);

  return (
    <div className="admin__confirm">
      <Input
        label="Department"
        placeholder="e.g. Marketing"
        value={department}
        onChange={(e) => setDepartment(e.target.value)}
      />

      <Input
        label="Job title"
        placeholder="e.g. Content Lead"
        value={jobTitle}
        onChange={(e) => setJobTitle(e.target.value)}
      />

      <Select
        label="Reports to"
        value={managerId}
        onChange={(e) => setManagerId(e.target.value)}
      >
        <option value="">Nobody</option>
        {managerOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.displayName}
          </option>
        ))}
      </Select>

      <Checkbox
        label="Required to submit a daily report"
        checked={checkInRequired}
        onChange={(e) => setCheckInRequired(e.target.checked)}
      />

      <div className="admin__confirm-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={saving}
          onClick={() =>
            onSave({
              // Empty text clears the field (stored as null), matching the
              // API's nullable department/jobTitle.
              department: department.trim() || null,
              jobTitle: jobTitle.trim() || null,
              managerId: managerId || null,
              checkInRequired,
            })
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
}

interface SetPasswordProps {
  user: AdminUser;
  onDone: () => void;
  onClose: () => void;
}

function SetPasswordDialog({ user, onDone, onClose }: SetPasswordProps) {
  const { toast } = useToast();
  // Generated once per open rather than on every render, so the value the
  // administrator is reading cannot change under them.
  const [password] = useState(generateTemporaryPassword);
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = useCallback(() => {
    setSaving(true);
    api
      .post(`/admin/users/${user.id}/set-password`, { password })
      .then(() => {
        setIssued(true);
        onDone();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not set that password.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [user.id, password, onDone, toast]);

  if (issued) {
    return (
      <div className="admin__confirm">
        <p className="admin-invite__done">
          <Icon name="check" size={15} />
          Temporary password set for <strong>{user.displayName}</strong>.
        </p>

        <label className="admin-invite__link-label" htmlFor="temp-password">
          Give them this password
        </label>
        <div className="admin-invite__link">
          <input id="temp-password" readOnly value={password} onFocus={(e) => e.target.select()} />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              navigator.clipboard
                .writeText(password)
                .then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                })
                .catch(() => toast("Could not copy — select it and copy manually.", { tone: "danger" }));
            }}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <p className="admin__confirm-warning">
          <Icon name="warning" size={13} />
          Hand this over in person or by a channel you trust — not by the email address it protects. They must choose
          their own password before they can use anything, and this one stops working then.
        </p>

        <div className="admin__confirm-actions">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin__confirm">
      <p>
        This replaces the password for <strong>{user.displayName}</strong> and signs them out everywhere. They will
        have to choose their own before they can use Astrum Connect again.
      </p>
      <p className="admin__confirm-warning">
        <Icon name="warning" size={13} />
        Only do this once you are sure who you are speaking to. There is no email verification behind it.
      </p>
      <div className="admin__confirm-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" loading={saving} onClick={submit}>
          Set temporary password
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────── main view ─────────────────────────────── */

/** Declaration order is tab order. */
/* ─────────────────────────────── offices ───────────────────────────────── */

/** Roles assignable per office, mirroring the server's memberBody enum. */
const OFFICE_ROLES = ["viewer", "member", "editor", "manager", "admin"] as const;

type Office = { id: string; name: string; slug: string; role: string; isDefault: boolean };

/**
 * Admin management of offices (workspaces): list them, create one, and add or
 * remove people with a per-office role.
 *
 * The list comes from GET /workspaces, which returns only the viewer's own
 * memberships — so this shows the offices the administrator already belongs to.
 * An office the admin is not a member of will not appear here; managing those
 * would need a dedicated admin-wide listing endpoint on the server.
 */
function OfficesPanel({ users }: { users: AdminUser[] }) {
  const { toast } = useToast();
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ workspaces: Office[] }>("/workspaces")
      .then((res) => setOffices(res.workspaces))
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load offices.", { tone: "danger" });
      })
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(() => {
    const trimmedName = name.trim();
    const trimmedSlug = slug.trim();
    if (!trimmedName || !trimmedSlug) return;
    setCreating(true);
    api
      .post("/workspaces", { name: trimmedName, slug: trimmedSlug })
      .then(() => {
        setName("");
        setSlug("");
        toast(`${trimmedName} created.`);
        load();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not create that office.", { tone: "danger" });
      })
      .finally(() => setCreating(false));
  }, [name, slug, toast, load]);

  return (
    <section className="admin__panel" aria-label="Offices">
      <form
        className="admin-invite"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <Input
          label="Office name"
          placeholder="China"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Input label="Slug" placeholder="china" value={slug} onChange={(e) => setSlug(e.target.value)} required />
        <div className="admin-invite__actions">
          <Button type="submit" loading={creating} disabled={!name.trim() || !slug.trim() || creating}>
            Create office
          </Button>
        </div>
      </form>

      {loading ? (
        <SkeletonText lines={3} />
      ) : offices.length === 0 ? (
        <EmptyState icon="people" title="No offices" description="Create one to get started." />
      ) : (
        <div className="admin-offices">
          {offices.map((office) => (
            <OfficeCard key={office.id} office={office} users={users} />
          ))}
        </div>
      )}
    </section>
  );
}

/** One person in an office, as GET /workspaces/:id/members returns them. */
type OfficeMember = { userId: string; role: string; displayName: string; email: string };

/**
 * One office as a card: its name and slug, a table of its members with an
 * inline role control and remove per row, and a footer row to add someone.
 *
 * Members come from GET /workspaces/:id/members (admin-only) rather than being
 * inferred client-side, so the table shows who is actually in the office. The
 * add/role/remove calls reuse the existing member endpoints; each refetches so
 * the table always reflects the server.
 */
function OfficeCard({ office, users }: { office: Office; users: AdminUser[] }) {
  const { toast } = useToast();
  const [members, setMembers] = useState<OfficeMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("member");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ members: OfficeMember[] }>(`/workspaces/${office.id}/members`)
      .then((res) => setMembers(res.members))
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load members.", { tone: "danger" });
      })
      .finally(() => setLoading(false));
  }, [office.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // People not already in this office — the only ones the add control offers,
  // so you cannot "add" someone who is already a member.
  const memberIds = new Set(members.map((m) => m.userId));
  const addable = users.filter((u) => !memberIds.has(u.id));

  const setMemberRole = useCallback(
    (userId: string, role: string) => {
      setBusyId(userId);
      api
        .post(`/workspaces/${office.id}/members`, { userId, role })
        .then(() => load())
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not change that role.", { tone: "danger" });
        })
        .finally(() => setBusyId(null));
    },
    [office.id, load, toast],
  );

  const removeMember = useCallback(
    (userId: string, name: string) => {
      setBusyId(userId);
      api
        .delete(`/workspaces/${office.id}/members/${userId}`)
        .then(() => {
          toast(`${name} removed from ${office.name}.`);
          load();
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not remove that person.", { tone: "danger" });
        })
        .finally(() => setBusyId(null));
    },
    [office.id, office.name, load, toast],
  );

  const addMember = useCallback(() => {
    if (!addUserId) return;
    setBusyId(addUserId);
    api
      .post(`/workspaces/${office.id}/members`, { userId: addUserId, role: addRole })
      .then(() => {
        const person = users.find((u) => u.id === addUserId);
        toast(`${person?.displayName ?? "Person"} added to ${office.name}.`);
        setAddUserId("");
        setAddRole("member");
        load();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not add that person.", { tone: "danger" });
      })
      .finally(() => setBusyId(null));
  }, [addUserId, addRole, office.id, office.name, users, load, toast]);

  return (
    <section className="admin-office" aria-label={office.name}>
      <header className="admin-office__head">
        <strong>{office.name}</strong>
        <Pill>{office.slug}</Pill>
        {office.isDefault && <Pill tone="info">Default</Pill>}
        <span className="admin-office__count">
          {members.length} {members.length === 1 ? "member" : "members"}
        </span>
      </header>

      {loading ? (
        <SkeletonText lines={2} />
      ) : (
        <div className="admin-office__table" aria-label={`${office.name} members`}>
          <div className="admin-office__row admin-office__row--head">
            <span>Person</span>
            <span>Role</span>
            <span className="admin-office__col-actions">
              Actions
            </span>
          </div>

          {members.length === 0 ? (
            <div className="admin-office__empty">No one in this office yet.</div>
          ) : (
            members.map((m) => (
              <div key={m.userId} className="admin-office__row">
                <div className="admin-office__person">
                  <strong>{m.displayName}</strong>
                  <span>{m.email}</span>
                </div>
                <Select
                  aria-label={`Role for ${m.displayName}`}
                  value={m.role}
                  disabled={busyId === m.userId}
                  onChange={(e) => setMemberRole(m.userId, e.target.value)}
                >
                  {OFFICE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r] ?? r}
                    </option>
                  ))}
                </Select>
                <div className="admin-office__col-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busyId === m.userId}
                    onClick={() => removeMember(m.userId, m.displayName)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))
          )}

          {/* Add-a-person footer, on the same grid as the rows above. */}
          <div className="admin-office__row admin-office__row--add">
            <Select
              aria-label="Add a person to this office"
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
              disabled={addable.length === 0}
            >
              <option value="">{addable.length === 0 ? "Everyone is in this office" : "Add a person…"}</option>
              {addable.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </Select>
            <Select aria-label="Role for the person being added" value={addRole} onChange={(e) => setAddRole(e.target.value)}>
              {OFFICE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r] ?? r}
                </option>
              ))}
            </Select>
            <div className="admin-office__col-actions">
              <Button type="button" onClick={addMember} loading={busyId === addUserId} disabled={!addUserId}>
                Add
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const TAB_LABELS = {
  people: "People",
  invitations: "Invitations",
  offices: "Offices",
  groups: "Groups",
  teams: "Teams",
  capacity: "Capacity",
  audit: "Audit log",
  settings: "Settings",
} as const;

export function Admin() {
  const { viewer } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState<keyof typeof TAB_LABELS>("people");
  usePageTitle(`Admin · ${TAB_LABELS[tab]}`);
  const [stats, setStats] = useState<Stats | undefined>(undefined);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState<AdminUser | null>(null);
  const [resettingTwoFactor, setResettingTwoFactor] = useState<AdminUser | null>(null);
  const [settingPassword, setSettingPassword] = useState<AdminUser | null>(null);
  const [editingReporting, setEditingReporting] = useState<AdminUser | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  /* Bulk selection: the one action every row shares regardless of its own
     locked/self/last-admin state is the daily-report check-in flag, so that
     is the only bulk action offered — role and activation stay per-row,
     mirroring PATCH /admin/users/bulk's own deliberately narrow scope. */
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkCheckIn, setBulkCheckIn] = useState<boolean | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  /** When the invitation list was fetched, for judging expiry without
   *  reading the clock during render. */
  const [loadedAt, setLoadedAt] = useState(() => Date.now());
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  /** Which invitation is being reissued, and the link that came back. */
  const [reissuingId, setReissuingId] = useState<string | null>(null);
  const [reissued, setReissued] = useState<{ email: string; link: string } | null>(null);

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const loadStats = useCallback(() => {
    api
      .get<Stats>("/admin/stats")
      .then(setStats)
      .catch(() => undefined);
  }, []);

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (includeInactive) params.set("includeInactive", "true");
    api
      .get<{ users: AdminUser[] }>(`/admin/users?${params}`)
      .then((res) => {
        setUsers(res.users);
        // A selection made against the previous result set may no longer
        // correspond to what is on screen — clearing it here rather than
        // trying to carry it across a search/filter change.
        setSelectedUserIds(new Set());
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load people.", { tone: "danger" });
      })
      .finally(() => setUsersLoading(false));
  }, [search, includeInactive, toast]);

  const loadInvitations = useCallback(() => {
    setInvitationsLoading(true);
    api
      .get<{ invitations: Invitation[] }>("/admin/invitations")
      .then((res) => {
        setInvitations(res.invitations);
        setLoadedAt(Date.now());
      })
      .catch(() => undefined)
      .finally(() => setInvitationsLoading(false));
  }, []);

  const [auditFilters, setAuditFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const auditQs = auditQuery(auditFilters);

  const loadAudit = useCallback(() => {
    setEventsLoading(true);
    api
      .get<{ events: AuditEvent[] }>(`/admin/audit?limit=100${auditQs ? `&${auditQs}` : ""}`)
      .then((res) => setEvents(res.events))
      .catch(() => undefined)
      .finally(() => setEventsLoading(false));
  }, [auditQs]);

  useEffect(() => loadStats(), [loadStats]);

  // Debounced so typing a name does not fire a request per keystroke. The
  // Offices tab also needs the people list to populate its member picker, so it
  // loads users too (the search box is not shown there, so it just refetches).
  useEffect(() => {
    if (tab !== "people" && tab !== "offices" && tab !== "audit") return;
    const timer = setTimeout(loadUsers, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [tab, loadUsers, search]);

  useEffect(() => {
    if (tab === "invitations") loadInvitations();
  }, [tab, loadInvitations]);

  // Debounced like the people search: typing an action name should not fire a
  // request per keystroke.
  useEffect(() => {
    if (tab !== "audit") return;
    const timer = setTimeout(loadAudit, 250);
    return () => clearTimeout(timer);
  }, [tab, loadAudit]);

  const activeAdmins = useMemo(
    () => users.filter((u) => u.role === "admin" && u.isActive).length,
    [users],
  );

  const patchUser = useCallback(
    (user: AdminUser, changes: Partial<Pick<AdminUser, "role" | "isActive" | "managerId" | "checkInRequired">>) => {
      setSavingUserId(user.id);
      api
        .patch<{ user: AdminUser }>(`/admin/users/${user.id}`, changes)
        .then((res) => {
          setUsers((prev) => prev.map((u) => (u.id === user.id ? res.user : u)));
          loadStats();
          toast(`${user.displayName} updated.`);
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not update that person.", { tone: "danger" });
        })
        .finally(() => setSavingUserId(null));
    },
    [loadStats, toast],
  );

  const applyBulkCheckIn = useCallback(() => {
    if (bulkCheckIn === null || selectedUserIds.size === 0) return;
    const ids = [...selectedUserIds];
    setBulkBusy(true);
    api
      .patch<{ updated: number }>("/admin/users/bulk", { userIds: ids, checkInRequired: bulkCheckIn })
      .then((res) => {
        toast(
          `${res.updated} ${res.updated === 1 ? "person" : "people"} ${bulkCheckIn ? "now required" : "no longer required"} to check in.`,
        );
        setBulkCheckIn(null);
        setSelectedUserIds(new Set());
        loadUsers();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not update those people.", { tone: "danger" });
      })
      .finally(() => setBulkBusy(false));
  }, [bulkCheckIn, selectedUserIds, toast, loadUsers]);

  const forceSignOut = useCallback(() => {
    if (!signingOut) return;
    const target = signingOut;
    setConfirmBusy(true);
    api
      .post(`/admin/users/${target.id}/sign-out`)
      .then(() => {
        setSigningOut(null);
        toast(`${target.displayName} was signed out on every device.`);
        // Signing yourself out ends this session too, so the next request
        // will 401 and the app returns to the sign-in screen on its own.
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not sign that person out.", { tone: "danger" });
      })
      .finally(() => setConfirmBusy(false));
  }, [signingOut, toast]);

  const resetTwoFactor = useCallback(() => {
    if (!resettingTwoFactor) return;
    const target = resettingTwoFactor;
    setConfirmBusy(true);
    api
      .post<{ user: AdminUser }>(`/admin/users/${target.id}/reset-two-factor`)
      .then((res) => {
        setUsers((prev) => prev.map((u) => (u.id === target.id ? res.user : u)));
        setResettingTwoFactor(null);
        loadStats();
        toast(`Two-factor removed for ${target.displayName}. They can set it up again.`);
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not reset that.", { tone: "danger" });
      })
      .finally(() => setConfirmBusy(false));
  }, [resettingTwoFactor, loadStats, toast]);

  const revokeInvitation = useCallback(() => {
    if (!revoking) return;
    const target = revoking;
    api
      .delete(`/admin/invitations/${target.id}`)
      .then(() => {
        setInvitations((prev) => prev.filter((i) => i.id !== target.id));
        setRevoking(null);
        loadStats();
        toast(`Invitation for ${target.email} revoked.`);
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not revoke that invitation.", { tone: "danger" });
      });
  }, [revoking, loadStats, toast]);

  /**
   * Mint a fresh link for an invitation whose link is gone.
   *
   * The old one stops working immediately, which is why this reads as "new
   * link" rather than "resend" — nothing is sent, and the previous link is not
   * still out there working alongside this one.
   */
  const reissue = useCallback(
    (invitation: Invitation) => {
      setReissuingId(invitation.id);
      api
        .post<{ token: string }>(`/admin/invitations/${invitation.id}/reissue`)
        .then((res) => {
          setReissued({ email: invitation.email, link: `${window.location.origin}/invite/${res.token}` });
          loadInvitations();
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not reissue that invitation.", { tone: "danger" });
        })
        .finally(() => setReissuingId(null));
    },
    [loadInvitations, toast],
  );

  return (
    <>
      <PageBody
        className="admin"
        title="Administration"
        subtitle="People, invitations, and the record of what has been done."
        actions={
          <Button icon={<Icon name="plus" size={14} />} onClick={() => setInviteOpen(true)}>
            Invite someone
          </Button>
        }
      >

        {stats && (
          <div className="admin__stats">
            <StatTile icon="people" value={stats.users.active} label="Active people" />
            <StatTile icon="lock" value={stats.users.admins} label="Administrators" />
            <StatTile icon="lock" value={stats.users.twoFactor} label="With two-factor" />
            <StatTile icon="send" value={stats.pendingInvitations} label="Pending invitations" />
          </div>
        )}

        <Tabs
          value={tab}
          onChange={(id) => setTab(id as keyof typeof TAB_LABELS)}
          tabs={(Object.keys(TAB_LABELS) as (keyof typeof TAB_LABELS)[]).map((key) => ({
            id: key,
            label: TAB_LABELS[key],
          }))}
        >
        {tab === "people" && (
          <section className="admin__panel" aria-label="People">
            <div className="admin__toolbar">
              <label className="admin__search">
                <Icon name="search" size={14} />
                <input
                  type="search"
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search people"
                />
              </label>
              <label className="admin__checkbox">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                Show deactivated
              </label>
            </div>

            {selectedUserIds.size > 0 && (
              <div className="admin__bulk-toolbar">
                <span className="admin__bulk-count">
                  {selectedUserIds.size} selected
                </span>
                <Button size="sm" variant="secondary" onClick={() => setBulkCheckIn(true)}>
                  Set check-in required
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setBulkCheckIn(false)}>
                  Set check-in not required
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedUserIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}

            {usersLoading ? (
              <div className="admin__loading">
                <SkeletonText lines={6} />
              </div>
            ) : users.length === 0 ? (
              <EmptyState icon="people" title="Nobody matches" description="Try a different name or email." />
            ) : (
              /* A list of rows, not a table: no column headers, and each row
                 carries its own controls rather than a shared column
                 structure. It previously claimed `role="table"` with no
                 columnheaders, which reads as a table a screen reader cannot
                 navigate. */
              <div className="admin__table" aria-label="People">
                {/* Header row on the same grid as every data row, so the two
                    dropdowns are finally distinguishable without clicking one
                    to find out which is which. */}
                <div className="admin__row admin__row--head">
                  <span className="admin__row-select">
                    <Checkbox
                      checked={users.length > 0 && users.every((u) => selectedUserIds.has(u.id))}
                      onChange={() =>
                        setSelectedUserIds((prev) =>
                          prev.size === users.length ? new Set() : new Set(users.map((u) => u.id)),
                        )
                      }
                      aria-label={`Select all ${users.length} people`}
                    />
                  </span>
                  <span aria-hidden="true" />
                  <span>Person</span>
                  <span>Details</span>
                  <span>Workspace role</span>
                  <span className="admin__col-actions">
                    Actions
                  </span>
                </div>
                {users.map((user) => {
                  const isSelf = user.id === viewer?.id;
                  // Mirrors the server's guards, so the reason is visible
                  // before the request rather than as an error after it.
                  const lastAdmin = user.role === "admin" && user.isActive && activeAdmins <= 1;
                  const locked = isSelf || lastAdmin;
                  const isSelected = selectedUserIds.has(user.id);

                  return (
                    <div key={user.id} className={`admin__row ${user.isActive ? "" : "is-inactive"}`}>
                      <span className="admin__row-select">
                        <Checkbox
                          checked={isSelected}
                          onChange={() =>
                            setSelectedUserIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(user.id)) next.delete(user.id);
                              else next.add(user.id);
                              return next;
                            })
                          }
                          aria-label={`Select ${user.displayName}`}
                        />
                      </span>
                      <Avatar name={user.displayName} src={user.avatarUrl ?? undefined} size={32} />

                      <div className="admin__person">
                        <strong>
                          {/* Wrapped so the name is what truncates. As a bare
                              text node beside the badge it could not shrink,
                              so a long name widened the whole column and
                              shifted every column after it on that row. */}
                          <span className="admin__person-name">{user.displayName}</span>
                          {isSelf && <span className="admin__you">you</span>}
                        </strong>
                        <span>{user.email}</span>
                      </div>

                      <div className="admin__person-meta">
                        {user.jobTitle && <span>{user.jobTitle}</span>}
                        {user.twoFactorEnabled && (
                          <Pill tone="ok">
                            <Icon name="lock" size={11} /> 2FA
                          </Pill>
                        )}
                        {!user.isActive && <Pill tone="neutral">Deactivated</Pill>}
                      </div>

                      <Select
                        aria-label={`Role for ${user.displayName}`}
                        className="admin__role"
                        value={user.role}
                        disabled={locked || savingUserId === user.id}
                        onChange={(e) => patchUser(user, { role: e.target.value })}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </Select>

                      <div className="admin__row-actions">
                        {locked ? (
                          <span
                            className="admin__locked"
                            title={
                              isSelf
                                ? "You cannot change your own access. Ask another administrator."
                                : "The last administrator cannot be demoted or deactivated."
                            }
                          >
                            <Icon name="lock" size={13} />
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant={user.isActive ? "ghost" : "secondary"}
                            loading={savingUserId === user.id}
                            onClick={() => patchUser(user, { isActive: !user.isActive })}
                          >
                            {user.isActive ? "Deactivate" : "Reactivate"}
                          </Button>
                        )}
                        {/* Security actions that are not about standing: ending
                            sessions and clearing a lost second factor. Kept in
                            a menu so the row stays scannable, and last so
                            it sits at the same x on every row, and available on
                            the last administrator too — locking those away is
                            what makes an account unrecoverable. */}
                        <Menu
                          open={openMenuId === user.id}
                          onClose={() => setOpenMenuId(null)}
                          items={[
                            {
                              id: "signout",
                              label: "Sign out everywhere",
                              icon: "lock",
                              onSelect: () => setSigningOut(user),
                            },
                            {
                              id: "password",
                              label: "Set a temporary password",
                              icon: "edit",
                              disabled: isSelf,
                              title: isSelf ? "Change your own password from your profile." : undefined,
                              onSelect: () => setSettingPassword(user),
                            },
                            {
                              id: "profile",
                              label: "Edit profile",
                              icon: "edit",
                              onSelect: () => setEditingReporting(user),
                            },
                            {
                              id: "twofactor",
                              label: "Reset two-factor",
                              icon: "warning",
                              disabled: !user.twoFactorEnabled,
                              title: user.twoFactorEnabled
                                ? undefined
                                : "This account does not have two-factor authentication on.",
                              onSelect: () => setResettingTwoFactor(user),
                            },
                          ]}
                        >
                          <IconButton
                            icon={<Icon name="more" size={16} />}
                            label={`Security actions for ${user.displayName}`}
                            size="sm"
                            onClick={() => setOpenMenuId((prev) => (prev === user.id ? null : user.id))}
                          />
                        </Menu>

                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {tab === "invitations" && (
          <section className="admin__panel" aria-label="Invitations">
            {invitationsLoading ? (
              <div className="admin__loading">
                <SkeletonText lines={4} />
              </div>
            ) : invitations.length === 0 ? (
              <EmptyState
                icon="people"
                title="No invitations yet"
                description="Invite a colleague to give them an account."
                action={<Button size="sm" onClick={() => setInviteOpen(true)}>Invite someone</Button>}
              />
            ) : (
              <div className="admin__table" aria-label="Invitations">
                <div className="admin__row admin__row--invite admin__row--head">
                  <span aria-hidden="true" />
                  <span>Invitee</span>
                  <span>Status</span>
                  <span className="admin__col-actions">
                    Actions
                  </span>
                </div>
                {invitations.map((invitation) => {
                  // Compared against the time the list was fetched, not "now":
                  // reading the clock during render makes the output depend on
                  // when React happens to re-run it.
                  const expired = new Date(invitation.expiresAt).getTime() < loadedAt;
                  const state = invitation.acceptedAt ? "accepted" : expired ? "expired" : "pending";

                  return (
                    <div key={invitation.id} className="admin__row admin__row--invite">
                      <Icon name="people" size={18} />

                      <div className="admin__person">
                        <strong>{invitation.email}</strong>
                        <span>
                          {ROLE_LABEL[invitation.role] ?? invitation.role} · invited by {invitation.invitedByName}{" "}
                          {formatWhen(invitation.createdAt)}
                        </span>
                      </div>

                      <Pill tone={state === "accepted" ? "ok" : state === "expired" ? "neutral" : "warn"}>
                        {state === "accepted" ? "Accepted" : state === "expired" ? "Expired" : "Pending"}
                      </Pill>

                      <div className="admin__row-actions">
                        {/*
                         * An accepted invitation is a historical record, so it
                         * offers nothing. The other two both do: a pending one
                         * whose link was lost needs a new one, and an expired
                         * one needs exactly the same thing — which is the more
                         * common case, since expiry is what people notice.
                         */}
                        {state !== "accepted" && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => reissue(invitation)}
                            disabled={reissuingId === invitation.id}
                          >
                            {reissuingId === invitation.id ? "Working…" : "New link"}
                          </Button>
                        )}
                        {state !== "accepted" && (
                          <IconButton
                            icon={<Icon name="trash" size={15} />}
                            label={`Revoke invitation for ${invitation.email}`}
                            size="sm"
                            onClick={() => setRevoking(invitation)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}



        {tab === "audit" && (
          <section className="admin__panel" aria-label="Audit log">
            <div className="admin__audit-tools">
              <AuditFilterBar filters={auditFilters} onChange={setAuditFilters} people={users} />
              {/* Every matching event, not the 100 on screen, filtered the same way. */}
              <ExportMenu
                kind="audit log"
                excelPath={`/exports/audit.xlsx${auditQs ? `?${auditQs}` : ""}`}
                onCsv={() =>
                  downloadExcel(
                    `/exports/audit.csv${auditQs ? `?${auditQs}` : ""}`,
                    excelFileName("audit log").replace(/\.xlsx$/, ".csv"),
                  )
                }
              />
            </div>
            {eventsLoading ? (
              <div className="admin__loading">
                <SkeletonText lines={8} />
              </div>
            ) : events.length === 0 ? (
              <EmptyState
                icon="document"
                title={auditQs ? "No matching events" : "Nothing recorded yet"}
                description={auditQs ? "Try widening the filters." : "Actions will appear here."}
              />
            ) : (
              <div className="admin__audit">
                {events.map((event) => (
                  <div key={event.id} className="admin__audit-row">
                    <span className="admin__audit-action">{humaniseAction(event.action)}</span>
                    <span className="admin__audit-actor">{event.actorName ?? "System"}</span>
                    <span className="admin__audit-resource">
                      {event.resourceType ?? ""}
                      {event.resourceType && event.ipAddress ? " · " : ""}
                      {event.ipAddress ?? ""}
                    </span>
                    <time dateTime={event.createdAt}>{formatWhen(event.createdAt)}</time>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "offices" && <OfficesPanel users={users} />}

        {tab === "groups" && <UserGroupsAdmin />}
        {tab === "teams" && <AdminTeams users={users} />}
        {tab === "capacity" && <BudgetRatesAdmin />}

        {tab === "settings" && (
          <section className="admin__panel" aria-label="Workspace settings">
            <WorkspaceSettings />
            <div className="admin__settings-divider" />
            <TwoFactorPolicy />
            {/* Below the workspace settings rather than in its own tab: an
                administrator setting a workspace up reads down this page once,
                and mail is part of that pass. */}
            <div className="admin__settings-divider" />
            <MailSettings />
          </section>
        )}
        </Tabs>
      </PageBody>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite someone">
        <InviteDialog
          onClose={() => {
            setInviteOpen(false);
            if (tab === "invitations") loadInvitations();
          }}
          onInvited={loadStats}
        />
      </Modal>

      {/* Closing this loses the link, which is the mistake it exists to
          recover from — but recoverably so, since reissuing again is one
          click away in the list behind it. */}
      <Modal open={!!reissued} onClose={() => setReissued(null)} title="New invitation link">
        {reissued && (
          <InviteLink
            email={reissued.email}
            link={reissued.link}
            reissued
            onClose={() => setReissued(null)}
          />
        )}
      </Modal>

      <Modal
        open={!!settingPassword}
        onClose={() => setSettingPassword(null)}
        title="Set a temporary password?"
      >
        {settingPassword && (
          <SetPasswordDialog
            key={settingPassword.id}
            user={settingPassword}
            onDone={loadUsers}
            onClose={() => setSettingPassword(null)}
          />
        )}
      </Modal>

      <Modal
        open={!!editingReporting}
        onClose={() => setEditingReporting(null)}
        title={editingReporting ? `Edit ${editingReporting.displayName}` : "Edit profile"}
      >
        {editingReporting && (
          <ProfileDialog
            key={editingReporting.id}
            user={editingReporting}
            allUsers={users}
            saving={savingUserId === editingReporting.id}
            onSave={(changes) => {
              patchUser(editingReporting, changes);
              setEditingReporting(null);
            }}
            onClose={() => setEditingReporting(null)}
          />
        )}
      </Modal>

      <Modal open={!!signingOut} onClose={() => setSigningOut(null)} title="Sign out everywhere?">
        <div className="admin__confirm">
          <p>
            Every device signed in as <strong>{signingOut?.displayName}</strong> will be signed out. They keep their
            access and can sign in again straight away
            {signingOut?.id === viewer?.id ? ", including this browser — you will be signed out too" : ""}.
          </p>
          <div className="admin__confirm-actions">
            <Button variant="ghost" onClick={() => setSigningOut(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={confirmBusy} onClick={forceSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!resettingTwoFactor}
        onClose={() => setResettingTwoFactor(null)}
        title="Reset two-factor authentication?"
      >
        <div className="admin__confirm">
          <p>
            <strong>{resettingTwoFactor?.displayName}</strong> will sign in with their password alone until they set up
            two-factor again from their own profile. They will also be signed out everywhere.
          </p>
          <p className="admin__confirm-warning">
            <Icon name="warning" size={13} />
            Only do this once you are sure who you are speaking to. Someone who talks you into resetting this is one
            password away from the account.
          </p>
          <div className="admin__confirm-actions">
            <Button variant="ghost" onClick={() => setResettingTwoFactor(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={confirmBusy} onClick={resetTwoFactor}>
              Reset two-factor
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkCheckIn !== null}
        onClose={() => setBulkCheckIn(null)}
        title={bulkCheckIn ? "Require daily check-in?" : "Remove daily check-in requirement?"}
      >
        <div className="admin__confirm">
          <p>
            {bulkCheckIn
              ? `${selectedUserIds.size} ${selectedUserIds.size === 1 ? "person" : "people"} will be required to submit a daily report.`
              : `${selectedUserIds.size} ${selectedUserIds.size === 1 ? "person" : "people"} will no longer be required to submit a daily report.`}
          </p>
          <div className="admin__confirm-actions">
            <Button variant="ghost" onClick={() => setBulkCheckIn(null)}>
              Cancel
            </Button>
            <Button loading={bulkBusy} onClick={applyBulkCheckIn}>
              {bulkCheckIn ? "Set required" : "Set not required"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!revoking} onClose={() => setRevoking(null)} title="Revoke this invitation?">
        <div className="admin__confirm">
          <p>
            The link sent to <strong>{revoking?.email}</strong> will stop working. You can invite them again
            afterwards.
          </p>
          <div className="admin__confirm-actions">
            <Button variant="ghost" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={revokeInvitation}>
              Revoke
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
